import { useState, useEffect, useRef, ReactNode, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Store,
  FileText,
  Settings,
  LogOut,
  TrendingUp,
  DollarSign,
  Activity,
  Menu,
  X,
  Edit,
  MoreHorizontal,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useShopifyIntegrationStatus } from '@/hooks/useShopifyIntegrationStatus';

interface DashboardProps {
  initialSection?: string;
  customContent?: ReactNode;
}

const Dashboard = ({ initialSection = 'dashboard', customContent }: DashboardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const { user, logout, adminSlug } = useAuth();
  const [activeSection, setActiveSection] = useState(initialSection);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lojas, setLojas] = useState<Array<{id: string, nome: string}>>([]);
  const [lojaExpandida, setLojaExpandida] = useState(false);
  const [editandoLoja, setEditandoLoja] = useState<string | null>(null);
  const [nomeEditando, setNomeEditando] = useState('');
  const [conexoes, setConexoes] = useState<Record<string, {
    shopify: boolean;
    checkouts: {
      nativo: boolean;
      yampi: boolean;
      zedy: boolean;
      adoorei: boolean;
    };
    ads: {
      facebook: boolean;
      googleAds: boolean;
      ga4: boolean;
    };
  }>>({});
  const [pixelInputs, setPixelInputs] = useState<Record<string, {
    facebook: string[];
    googleAds: string;
    ga4: string;
  }>>({});
  const [showPixelInput, setShowPixelInput] = useState<Record<string, {
    facebook: boolean;
    googleAds: boolean;
    ga4: boolean;
  }>>({});
  const [pixelEditando, setPixelEditando] = useState<Record<string, number | null>>({});
  const [pixelEditInput, setPixelEditInput] = useState<Record<string, string>>({});
  const [currentPixelInput, setCurrentPixelInput] = useState<Record<string, string>>({});

  const slugForNav = routeSlug ?? adminSlug ?? 'app';
  const isShopifyRoute = useMemo(
    () => location.pathname.startsWith(`/admin/${slugForNav}/integrations/shopify`),
    [location.pathname, slugForNav],
  );
  const { integration: shopifyIntegration, loading: shopifyLoading } = useShopifyIntegrationStatus();

  // Gerenciar expansão do menu baseado na seção ativa
  useEffect(() => {
    if (activeSection === 'clientes' || lojas.some(loja => loja.id === activeSection)) {
      setLojaExpandida(true);
    } else {
      setLojaExpandida(false);
    }
  }, [activeSection, lojas]);

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  useEffect(() => {
    if (isShopifyRoute) {
      setActiveSection('shopify-integration');
      setLojaExpandida(true);
    }
  }, [isShopifyRoute]);

  const navigation = [
    { id: 'dashboard', name: 'Início', icon: LayoutDashboard },
    { id: 'clientes', name: 'Loja virtual', icon: Store },
    { id: 'relatorios', name: 'Relatórios', icon: FileText },
  ];

  const stats = [
    {
      title: 'Receita Total',
      value: 'R$ 45.231',
      change: '+20.1%',
      trend: 'up',
      icon: DollarSign,
    },
    {
      title: 'Clientes Ativos',
      value: '2.350',
      change: '+180',
      trend: 'up',
      icon: Store,
    },
    {
      title: 'Taxa Conversão',
      value: '12.5%',
      change: '+4.3%',
      trend: 'up',
      icon: TrendingUp,
    },
    {
      title: 'Atividade',
      value: '98.2%',
      change: '+2.1%',
      trend: 'up',
      icon: Activity,
    },
  ];

  
  // Função para iniciar edição da loja
  const iniciarEdicaoLoja = (lojaId: string, nomeAtual: string) => {
    setEditandoLoja(lojaId);
    setNomeEditando(nomeAtual);
  };

  // Função para salvar edição da loja
  const salvarEdicaoLoja = (lojaId: string) => {
    if (nomeEditando.trim()) {
      setLojas(lojas.map(loja => 
        loja.id === lojaId 
          ? { ...loja, nome: nomeEditando.trim() }
          : loja
      ));
    }
    setEditandoLoja(null);
    setNomeEditando('');
  };

  // Função para cancelar edição da loja
  const cancelarEdicaoLoja = () => {
    setEditandoLoja(null);
    setNomeEditando('');
  };

  // Função para deletar loja
  const deletarLoja = (lojaId: string) => {
    setLojas(lojas.filter(loja => loja.id !== lojaId));
    // Remove as conexões da loja deletada
    const novasConexoes = { ...conexoes };
    delete novasConexoes[lojaId];
    setConexoes(novasConexoes);
    // Se a loja deletada estava ativa, voltar para a seção clientes
    if (activeSection === lojaId) {
      setActiveSection('clientes');
    }
  };

  // Função para conectar serviço
  const conectarServico = (lojaId: string, categoria: 'shopify' | 'checkouts' | 'ads', servico?: string) => {
    if (categoria === 'ads') {
      // Para todas as plataformas de ads, sobe para o card principal mas ainda não está "conectado"
      setConexoes(prev => ({
        ...prev,
        [lojaId]: {
          shopify: prev[lojaId]?.shopify || false,
          checkouts: prev[lojaId]?.checkouts || { nativo: false, yampi: false, zedy: false, adoorei: false },
          ads: {
            ...prev[lojaId]?.ads,
            [servico!]: false  // False até configurar adequadamente
          }
        }
      }));
      
      // Inicializa APENAS a plataforma específica selecionada
      if (servico === 'facebook') {
        setPixelInputs(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            facebook: []
          }
        }));
      } else if (servico === 'googleAds') {
        setPixelInputs(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            googleAds: ''
          }
        }));
      } else if (servico === 'ga4') {
        setPixelInputs(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            ga4: ''
          }
        }));
      }
      return;
    }

    setConexoes(prev => ({
      ...prev,
      [lojaId]: {
        shopify: prev[lojaId]?.shopify || false,
        checkouts: prev[lojaId]?.checkouts || { nativo: false, yampi: false, zedy: false, adoorei: false },
        ads: prev[lojaId]?.ads || { facebook: false, googleAds: false, ga4: false },
        ...prev[lojaId],
        [categoria]: categoria === 'shopify' 
          ? true 
          : {
            ...prev[lojaId]?.[categoria],
            [servico!]: true
          }
      }
    }));
  };

  // Função para salvar pixel e conectar
  const salvarPixel = (lojaId: string, plataforma: 'facebook' | 'googleAds' | 'ga4') => {
    if (plataforma === 'facebook') {
      const pixelId = currentPixelInput[lojaId];
      if (pixelId?.trim()) {
        setPixelInputs(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            facebook: [...(prev[lojaId]?.facebook || []), pixelId.trim()],
            googleAds: prev[lojaId]?.googleAds || '',
            ga4: prev[lojaId]?.ga4 || ''
          }
        }));
        setConexoes(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            ads: {
              ...prev[lojaId]?.ads,
              facebook: true
            }
          }
        }));
        setCurrentPixelInput(prev => ({
          ...prev,
          [lojaId]: ''
        }));
        setShowPixelInput(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            facebook: false
          }
        }));
      }
    } else {
      // Para Google Ads e GA4
      const pixelId = currentPixelInput[lojaId];
      if (pixelId?.trim()) {
        setPixelInputs(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            facebook: prev[lojaId]?.facebook || [],
            googleAds: prev[lojaId]?.googleAds || '',
            ga4: prev[lojaId]?.ga4 || '',
            [plataforma]: pixelId.trim()
          }
        }));
        setConexoes(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            ads: {
              ...prev[lojaId]?.ads,
              [plataforma]: true
            }
          }
        }));
        setCurrentPixelInput(prev => ({
          ...prev,
          [lojaId]: ''
        }));
        setShowPixelInput(prev => ({
          ...prev,
          [lojaId]: {
            ...prev[lojaId],
            [plataforma]: false
          }
        }));
      }
    }
  };

  // Função para remover pixel específico do Facebook
  const removerPixel = (lojaId: string, pixelId: string) => {
    setPixelInputs(prev => {
      const novosFacebookPixels = prev[lojaId]?.facebook?.filter(id => id !== pixelId) || [];
      return {
        ...prev,
        [lojaId]: {
          ...prev[lojaId],
          facebook: novosFacebookPixels,
          googleAds: prev[lojaId]?.googleAds || '',
          ga4: prev[lojaId]?.ga4 || ''
        }
      };
    });
    
    // Se não há mais pixels, desconectar o Facebook
    const pixelsRestantes = pixelInputs[lojaId]?.facebook?.filter(id => id !== pixelId) || [];
    if (pixelsRestantes.length === 0) {
      setConexoes(prev => ({
        ...prev,
        [lojaId]: {
          ...prev[lojaId],
          ads: {
            ...prev[lojaId]?.ads,
            facebook: false
          }
        }
      }));
    }
  };

  // Função para iniciar edição de pixel individual
  const iniciarEdicaoPixel = (lojaId: string, pixelIndex: number, pixelAtual: string) => {
    setPixelEditando(prev => ({ ...prev, [lojaId]: pixelIndex }));
    setPixelEditInput(prev => ({ ...prev, [lojaId]: pixelAtual }));
  };

  // Função para salvar edição de pixel individual
  const salvarEdicaoPixel = (lojaId: string, pixelIndex: number) => {
    const novoPixel = pixelEditInput[lojaId];
    if (novoPixel?.trim()) {
      setPixelInputs(prev => ({
        ...prev,
        [lojaId]: {
          ...prev[lojaId],
          facebook: prev[lojaId]?.facebook?.map((pixel, index) => 
            index === pixelIndex ? novoPixel.trim() : pixel
          ) || [],
          googleAds: prev[lojaId]?.googleAds || '',
          ga4: prev[lojaId]?.ga4 || ''
        }
      }));
    }
    setPixelEditando(prev => ({ ...prev, [lojaId]: null }));
    setPixelEditInput(prev => ({ ...prev, [lojaId]: '' }));
  };

  // Função para cancelar edição de pixel individual
  const cancelarEdicaoPixel = (lojaId: string) => {
    setPixelEditando(prev => ({ ...prev, [lojaId]: null }));
    setPixelEditInput(prev => ({ ...prev, [lojaId]: '' }));
  };

  // Função para remover todos os pixels do Facebook
  const removerTodosPixels = (lojaId: string) => {
    setPixelInputs(prev => ({
      ...prev,
      [lojaId]: {
        ...prev[lojaId],
        facebook: [],
        googleAds: prev[lojaId]?.googleAds || '',
        ga4: prev[lojaId]?.ga4 || ''
      }
    }));
    setConexoes(prev => ({
      ...prev,
      [lojaId]: {
        ...prev[lojaId],
        ads: {
          ...prev[lojaId]?.ads,
          facebook: false
        }
      }
    }));
  };

  // Função para cancelar input do pixel
  const cancelarPixelInput = (lojaId: string, plataforma: 'facebook' | 'googleAds' | 'ga4') => {
    setShowPixelInput(prev => ({
      ...prev,
      [lojaId]: {
        ...prev[lojaId],
        [plataforma]: false
      }
    }));
    setPixelInputs(prev => ({
      ...prev,
      [lojaId]: {
        ...prev[lojaId],
        [plataforma]: ''
      }
    }));
  };

  // Função para desconectar serviço
  const desconectarServico = (lojaId: string, categoria: 'shopify' | 'checkouts' | 'ads', servico?: string) => {
    setConexoes(prev => ({
      ...prev,
      [lojaId]: {
        ...prev[lojaId],
        [categoria]: categoria === 'shopify' 
          ? false 
          : {
            ...prev[lojaId]?.[categoria],
            [servico!]: false
          }
      }
    }));
  };

  const handleLogout = () => {
    logout();
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Início</h2>
              <p className="text-muted-foreground">
                Visão geral das suas métricas e atividades.
              </p>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat) => (
                <Card key={stat.title} className="shadow-card hover:shadow-elegant transition-shadow duration-300">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {stat.title}
                    </CardTitle>
                    <stat.icon className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <p className="text-xs text-muted-foreground">
                      <span className="text-green-600">{stat.change}</span> em relação ao mês anterior
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
              <Card className="col-span-4 shadow-card">
                <CardHeader>
                  <CardTitle>Atividade Recente</CardTitle>
                </CardHeader>
                <CardContent className="pl-2">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src="/avatars/01.png" alt="Avatar" />
                        <AvatarFallback>JD</AvatarFallback>
                      </Avatar>
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">
                          João Silva fez uma nova compra
                        </p>
                        <p className="text-sm text-muted-foreground">
                          há 2 minutos
                        </p>
                      </div>
                      <div className="ml-auto font-medium">+R$ 1.999</div>
                    </div>
                    <div className="flex items-center">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src="/avatars/02.png" alt="Avatar" />
                        <AvatarFallback>MS</AvatarFallback>
                      </Avatar>
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">
                          Maria Santos se registrou
                        </p>
                        <p className="text-sm text-muted-foreground">
                          há 1 hora
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src="/avatars/03.png" alt="Avatar" />
                        <AvatarFallback>PS</AvatarFallback>
                      </Avatar>
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">
                          Pedro Oliveira atualizou perfil
                        </p>
                        <p className="text-sm text-muted-foreground">
                          há 3 horas
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="col-span-3 shadow-card">
                <CardHeader>
                  <CardTitle>Vendas Recentes</CardTitle>
                  <CardDescription>
                    Você fez 265 vendas este mês.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">Produto A</p>
                        <p className="text-sm text-muted-foreground">
                          Vendido há 5 min
                        </p>
                      </div>
                      <div className="ml-auto font-medium">R$ 250</div>
                    </div>
                    <div className="flex items-center">
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">Produto B</p>
                        <p className="text-sm text-muted-foreground">
                          Vendido há 1 hora
                        </p>
                      </div>
                      <div className="ml-auto font-medium">R$ 450</div>
                    </div>
                    <div className="flex items-center">
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">Produto C</p>
                        <p className="text-sm text-muted-foreground">
                          Vendido há 2 horas
                        </p>
                      </div>
                      <div className="ml-auto font-medium">R$ 320</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      case 'clientes':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Loja virtual</h2>
              <p className="text-muted-foreground">
                Gerencie suas lojas virtuais e operações.
              </p>
            </div>
            
            {lojas.length === 0 ? (
              // Tela quando não há lojas cadastradas
              <Card className="shadow-card">
                <CardContent className="p-8 text-center">
                  <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
                    <Store className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Nenhuma loja virtual encontrada</h3>
                  <p className="text-muted-foreground mb-6">
                    Adicione sua primeira loja virtual para começar a gerenciar seus produtos e vendas.
                  </p>
                  <Button 
                    onClick={() => {
                      const novaLoja = {
                        id: `loja-${Date.now()}`,
                        nome: `Minha Loja ${lojas.length + 1}`
                      };
                      setLojas([...lojas, novaLoja]);
                      setLojaExpandida(true);
                      setActiveSection(novaLoja.id);
                    }}
                    className="px-8"
                  >
                    <Store className="mr-2 h-4 w-4" />
                    Adicionar loja virtual
                  </Button>
                </CardContent>
              </Card>
            ) : (
              // Lista de lojas quando existem
              <div className="space-y-4">
                {lojas.map((loja) => (
                  <Card key={loja.id} className="shadow-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <Store className="w-6 h-6" />
                        {loja.nome}
                      </CardTitle>
                      <CardDescription>
                        Configurações e métricas da loja virtual.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
                
                <Button 
                  variant="outline"
                  onClick={() => {
                    const novaLoja = {
                      id: `loja-${Date.now()}`,
                      nome: `Minha Loja ${lojas.length + 1}`
                    };
                    setLojas([...lojas, novaLoja]);
                    setActiveSection(novaLoja.id);
                  }}
                  className="w-full"
                >
                  <Store className="mr-2 h-4 w-4" />
                  Adicionar nova loja virtual
                </Button>
              </div>
            )}
          </div>
        );
      default:
        // Verifica se é uma loja específica
        const lojaAtual = lojas.find(loja => loja.id === activeSection);
        if (lojaAtual) {
          const conexoesLoja = conexoes[lojaAtual.id] || {
            shopify: false,
            checkouts: { nativo: false, yampi: false, zedy: false, adoorei: false },
            ads: { facebook: false, googleAds: false, ga4: false }
          };

          return (
            <div className="space-y-6">
              {/* Card do Shopify */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <img src="/src/assets/shopify-icon.svg" alt="Shopify" className="w-6 h-6" />
                    Shopify (OAuth)
                  </CardTitle>
                  <CardDescription>
                    Conecte ou gerencie sua loja Shopify via OAuth seguro.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Abra a página dedicada para conectar, ver status ou desconectar sua loja.
                  </p>
                  <Button
                    className="px-6"
                    onClick={() =>
                      navigate(`/admin/${routeSlug ?? adminSlug ?? "app"}/integrations/shopify`)
                    }
                  >
                    <img src="/src/assets/shopify-icon.svg" alt="Shopify" className="w-4 h-4 mr-2" />
                    Gerenciar Shopify
                  </Button>
                </CardContent>
              </Card>

              {/* Card dos Checkouts - só aparece quando Shopify está conectado E nenhum checkout foi selecionado */}
              {conexoesLoja.shopify && !conexoesLoja.checkouts.nativo && !conexoesLoja.checkouts.yampi && !conexoesLoja.checkouts.zedy && !conexoesLoja.checkouts.adoorei && (
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle>Checkouts</CardTitle>
                    <CardDescription>
                      Configure sua plataforma de checkout preferida.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                           onClick={() => conectarServico(lojaAtual.id, 'checkouts', 'nativo')}>
                        <div className="flex justify-center mb-3">
                          <img src="/src/assets/shopify-icon.svg" alt="Shopify Nativo" className="w-8 h-8" />
                        </div>
                        <p className="text-sm font-medium mb-1">Nativo Shopify</p>
                        <p className="text-sm font-medium text-primary">Conectar</p>
                      </div>
                      
                      <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                           onClick={() => conectarServico(lojaAtual.id, 'checkouts', 'yampi')}>
                        <div className="flex justify-center mb-3">
                          <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-bold">Y</div>
                        </div>
                        <p className="text-sm font-medium mb-1">Yampi</p>
                        <p className="text-sm font-medium text-primary">Conectar</p>
                      </div>
                      
                      <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                           onClick={() => conectarServico(lojaAtual.id, 'checkouts', 'zedy')}>
                        <div className="flex justify-center mb-3">
                          <div className="w-8 h-8 bg-purple-500 rounded flex items-center justify-center text-white text-xs font-bold">Z</div>
                        </div>
                        <p className="text-sm font-medium mb-1">Zedy Checkout</p>
                        <p className="text-sm font-medium text-primary">Conectar</p>
                      </div>
                      
                      <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                           onClick={() => conectarServico(lojaAtual.id, 'checkouts', 'adoorei')}>
                        <div className="flex justify-center mb-3">
                          <div className="w-8 h-8 bg-pink-500 rounded flex items-center justify-center text-white text-xs font-bold">A</div>
                        </div>
                        <p className="text-sm font-medium mb-1">Adoorei</p>
                        <p className="text-sm font-medium text-primary">Conectar</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Card das Plataformas de Anúncios - só aparece quando Shopify está conectado e nem todas as plataformas foram conectadas */}
              {conexoesLoja.shopify && !(
                pixelInputs[lojaAtual.id]?.facebook !== undefined &&
                pixelInputs[lojaAtual.id]?.googleAds !== undefined &&
                pixelInputs[lojaAtual.id]?.ga4 !== undefined
              ) && (
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle>Plataformas de Anúncios</CardTitle>
                    <CardDescription>
                      Conecte plataformas de publicidade para impulsionar suas vendas.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Facebook Ads - só aparece se não foi conectado ainda */}
                      {pixelInputs[lojaAtual.id]?.facebook === undefined && (
                        <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                             onClick={() => conectarServico(lojaAtual.id, 'ads', 'facebook')}>
                          <div className="flex justify-center mb-3">
                            <img src="/src/assets/meta-logo.svg" alt="Facebook/Meta" className="w-12 h-8 object-contain" />
                          </div>
                          <p className="text-sm font-medium mb-1">Facebook Ads</p>
                          <p className="text-sm font-medium text-primary">Conectar</p>
                        </div>
                      )}
                      
                      {/* Google Ads - só aparece se não foi conectado ainda */}
                      {pixelInputs[lojaAtual.id]?.googleAds === undefined && (
                        <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                             onClick={() => conectarServico(lojaAtual.id, 'ads', 'googleAds')}>
                          <div className="flex justify-center mb-3">
                            <img src="/src/assets/google-ads.png" alt="Google Ads" className="w-8 h-8 object-contain" />
                          </div>
                          <p className="text-sm font-medium mb-1">Google Ads</p>
                          <p className="text-sm font-medium text-primary">Conectar</p>
                        </div>
                      )}
                      
                      {/* GA4 - só aparece se não foi conectado ainda */}
                      {pixelInputs[lojaAtual.id]?.ga4 === undefined && (
                        <div className="text-center p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                             onClick={() => conectarServico(lojaAtual.id, 'ads', 'ga4')}>
                          <div className="flex justify-center mb-3">
                            <img src="/src/assets/ga4-logo.png" alt="Google Analytics 4" className="w-12 h-10 object-contain" />
                          </div>
                          <p className="text-sm font-medium mb-1">Google Analytics 4</p>
                          <p className="text-sm font-medium text-primary">Conectar</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          );
        }
        
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                {navigation.find(item => item.id === activeSection)?.name}
              </h2>
              <p className="text-muted-foreground">
                Esta seção está em desenvolvimento.
              </p>
            </div>
            <Card className="shadow-card">
              <CardContent className="p-6">
                <p className="text-center text-muted-foreground">
                  Conteúdo em breve...
                </p>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-dashboard-bg">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-dashboard-sidebar transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between px-6 h-12 border-b border-dashboard-border" style={{backgroundColor: '#1A1A1A'}}>
          <h1 className="text-lg font-bold" style={{color: '#FFFFFF'}}>
            Painel de Controle
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <nav className="px-4 pt-3 space-y-1">
          {navigation.map((item) => {
            const isClientes = item.id === 'clientes';
            const isActive = activeSection === item.id || (isClientes && isShopifyRoute);

            if (isClientes) {
              return (
                <div key={item.id} className="relative">
                  <div className="flex items-center gap-1 px-2 pb-1">
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className="flex-1 justify-start h-8 px-3 py-1 text-sm nav-main"
                      onClick={() => {
                        setActiveSection('shopify-integration');
                        setLojaExpandida(true);
                        setSidebarOpen(false);
                        navigate(`/admin/${slugForNav}/integrations/shopify`);
                      }}
                    >
                      <item.icon className="mr-3 h-4 w-4 nav-icon" id="mainIcon-loja-virtual" />
                      {item.name}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLojaExpandida((prev) => !prev)}
                      aria-label={lojaExpandida ? 'Recolher Loja virtual' : 'Expandir Loja virtual'}
                    >
                      {lojaExpandida ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>

                  {lojaExpandida && (
                    <div className="space-y-1 submenu-loja pl-2">
                      {shopifyIntegration ? (
                        <Button
                          variant={isShopifyRoute ? "secondary" : "ghost"}
                          className="w-full justify-start h-8 px-3 py-1 text-sm"
                          onClick={() => {
                            setActiveSection('shopify-integration');
                            setSidebarOpen(false);
                            navigate(`/admin/${slugForNav}/integrations/shopify`);
                          }}
                        >
                          <span className="mr-2 h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.25)]" />
                          <span className="font-mono text-sm">{shopifyIntegration.shop_domain}</span>
                        </Button>
                      ) : (
                        <div className="w-full justify-start h-8 px-3 py-1 text-sm text-muted-foreground flex items-center">
                          {shopifyLoading ? "Carregando..." : "Nenhuma loja conectada"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={item.id}>
                <Button
                  variant={isActive ? "secondary" : "ghost"}
                  className="w-full justify-start h-8 px-3 py-1 text-sm nav-main"
                  onClick={() => {
                    if (item.id === 'dashboard') {
                      setActiveSection('dashboard');
                      setSidebarOpen(false);
                      navigate(`/admin/${slugForNav}`);
                    } else if (item.id === 'relatorios') {
                      setActiveSection('relatorios');
                      setSidebarOpen(false);
                      navigate(`/admin/${slugForNav}/reports`);
                    }
                  }}
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  {item.name}
                </Button>
              </div>
            );
          })}
        </nav>

        {/* Bottom actions: Settings + User, sticky bottom like Shopify */}
        <div className="absolute bottom-0 w-full bg-dashboard-sidebar">
          <div className="px-4 py-2">
            <Button
              variant={activeSection === 'configuracoes' ? "secondary" : "ghost"}
              className="w-full justify-start h-8 px-3 py-1 text-sm"
              onClick={() => {
                setActiveSection('configuracoes');
                setSidebarOpen(false);
              }}
            >
              <img src="/src/assets/settings-shopify.svg" alt="Configurações" className="mr-3 h-4 w-4" />
              Configurações
            </Button>
          </div>
          <div className="p-4 border-t border-dashboard-border">
            <div className="flex items-center space-x-4 mb-4">
              <Avatar>
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback>{user?.name?.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Contínuo */}
        <header className="px-4 py-2 lg:px-6 h-12 flex items-center justify-between border-b border-dashboard-border" style={{backgroundColor: '#1A1A1A'}}>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-4 w-4" />
          </Button>
          <div className="flex items-center space-x-4">
            <span className="text-sm" style={{color: '#FFFFFF'}}>
              Bem-vindo, {user?.name}!
            </span>
          </div>
        </header>

        {/* Verificar se é uma loja específica para mostrar header secundário */}
        {lojas.find(loja => loja.id === activeSection) && (
          <div className="border-b border-dashboard-border bg-background px-4 py-3 lg:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Store className="w-5 h-5 text-primary" />
                <h1 className="text-xl font-semibold">
                  {lojas.find(loja => loja.id === activeSection)?.nome}
                </h1>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-[#E5E5E5] transition-colors"
                  >
                    <img src="/src/assets/svg_3_pontinhos_shopify.svg" alt="Menu" className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white z-50">
                  <DropdownMenuItem 
                    onClick={() => deletarLoja(lojas.find(loja => loja.id === activeSection)?.id || '')}
                    className="cursor-pointer"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#FED9DF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <img 
                      src="/src/assets/svg_da_lixeira_deletar_shopify.svg" 
                      alt="Deletar" 
                      className="h-4 w-4 mr-2"
                      style={{ filter: 'brightness(0) saturate(100%) invert(8%) sepia(69%) saturate(7478%) hue-rotate(340deg) brightness(97%) contrast(117%)' }}
                    />
                    <span style={{ color: '#8E0B21', fontWeight: '600' }}>Deletar</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-4xl mx-auto">
            {customContent ?? renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;